import { useCallback } from 'react';

import { Button, EditFilled, Box, Tooltip } from '@aircall/tractor';
import { AgentRolesPicker } from '@components/AgentRolesPicker/AgentRolesPicker';
import { ModalForm } from '@components/ModalForm';
import { RolesTags } from '@components/RolesTags';
import { Gap } from '@dashboard/library';
import { GetAllRolesQuery_getAllRoles } from '@generated/GetAllRolesQuery';
import { useSetRoles } from '@hooks/useSetRoles';
import { useToggle } from '@hooks/useToggle/useToggle';
import { UserProfileTabProps } from '@pages/user-profile/UserProfile.decl';
import debounce from 'lodash-es/debounce';
import { useTranslation } from 'react-i18next';

interface RolesDisplayProps extends UserProfileTabProps {
  isCurrentUser: boolean;
}

interface FormFields {
  roles: GetAllRolesQuery_getAllRoles[];
}

export function EditRolesContainer({ user, isCurrentUser }: RolesDisplayProps) {
  const [isOpen, _, toggleOpen, hide] = useToggle();
  const { t } = useTranslation();

  const { setRoles } = useSetRoles();

  const onSubmit = useCallback(
    async (input: FormFields) => {
      const response = await setRoles({ roles: input.roles, userId: user.ID });

      if (response) {
        return response;
      }

      debounce(hide, 3000)();
      return undefined;
    },
    [setRoles, user.ID, hide]
  );

  return (
    <>
      <ModalForm
        size='small'
        show={isOpen}
        title={t('app.roles.label')}
        onHide={hide}
        submitButtonText={t('update_role_modal.update_user')}
        onCancelClick={hide}
        rootClose
        formProps={{
          onSubmit,
          initialValues: { roles: user.roles },
        }}
      >
        {({ submitting }) => (
          <Box padding='m'>
            <AgentRolesPicker disabled={submitting} name='roles' showInfoBanner />
          </Box>
        )}
      </ModalForm>

      <Gap alignItems='center' gap='m' flexWrap='wrap'>
        <RolesTags roles={user.roles} />

        {isCurrentUser ? (
          <Tooltip
            title={t('update_role_modal.disabled_button_tooltip')}
            mouseLeaveDelay={0}
            position='top'
            anchor='center'
          >
            <Box display='inline-block'>
              <Button
                data-test='edit-roles-button'
                mode='link'
                onClick={toggleOpen}
                disabled={isCurrentUser}
              >
                <EditFilled />
                {t('update_role_modal.edit_button')}
              </Button>
            </Box>
          </Tooltip>
        ) : (
          <Button mode='link' onClick={toggleOpen} data-test='edit-roles-button'>
            <EditFilled /> {t('update_role_modal.edit_button')}
          </Button>
        )}
      </Gap>
    </>
  );
}
